/**
 * LightningRiskLayer
 *
 * Displays lightning risk as colored rectangles on the 2D Leaflet map.
 * Shows areas where atmospheric conditions favor thunderstorms and fire ignition.
 *
 * Data source: NOAA GFS (Global Forecast System) via NOMADS
 * Risk is calculated from CAPE (Convective Available Potential Energy):
 * - Extreme: >= 2500 J/kg (very high storm potential)
 * - High: >= 1500 J/kg (significant storm potential)
 * - Moderate: >= 750 J/kg (some storm potential)
 * - Low: >= 250 J/kg (minimal storm potential)
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Rectangle, useMap, Popup } from 'react-leaflet';

interface RiskPoint {
  lat: number;
  lon: number;
  cape: number;
  riskLevel: 'minimal' | 'low' | 'moderate' | 'high' | 'extreme';
  riskScore: number;
}

interface LightningRiskData {
  type: string;
  points: RiskPoint[];
  generated: string;
  source: string;
  gfsRun?: string;
  resolution: number;
}

interface LightningRiskLayerProps {
  enabled: boolean;
  minRiskLevel?: 'low' | 'moderate' | 'high';
  onDataLoaded?: (data: LightningRiskData | null) => void;
}

const RISK_URL = import.meta.env.VITE_LIGHTNING_RISK_URL ||
  `${import.meta.env.VITE_STATIC_URL || 'https://d3c2yw8hurho5b.cloudfront.net'}/lightning/risk-grid.json`;

// Risk level colors
const RISK_COLORS: Record<string, { fill: string; opacity: number }> = {
  minimal: { fill: '#22c55e', opacity: 0.08 },   // Very light green (low risk baseline)
  low: { fill: '#84cc16', opacity: 0.15 },       // Light green
  moderate: { fill: '#eab308', opacity: 0.25 },  // Yellow
  high: { fill: '#f97316', opacity: 0.35 },      // Orange
  extreme: { fill: '#dc2626', opacity: 0.45 }    // Red
};

const RISK_LEVEL_ORDER = ['minimal', 'low', 'moderate', 'high', 'extreme'];

export const LightningRiskLayer: React.FC<LightningRiskLayerProps> = ({
  enabled,
  minRiskLevel = 'minimal',
  onDataLoaded
}) => {
  const [riskData, setRiskData] = useState<LightningRiskData | null>(null);
  useMap(); // Hook ensures component re-renders on map changes

  const minLevelIndex = RISK_LEVEL_ORDER.indexOf(minRiskLevel);

  const fetchRiskData = useCallback(async () => {
    if (!enabled) return;

    try {
      const response = await fetch(RISK_URL);
      if (!response.ok) {
        console.warn('No lightning risk data available');
        setRiskData(null);
        onDataLoaded?.(null);
        return;
      }

      const data = await response.json();
      setRiskData(data);
      onDataLoaded?.(data);

    } catch (error) {
      console.warn('Error fetching lightning risk data:', error);
      setRiskData(null);
      onDataLoaded?.(null);
    }
  }, [enabled, onDataLoaded]);

  useEffect(() => {
    if (!enabled) {
      setRiskData(null);
      return;
    }

    fetchRiskData();

    // Refresh every 15 minutes
    const interval = setInterval(fetchRiskData, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [enabled, fetchRiskData]);

  if (!enabled || !riskData?.points) return null;

  const resolution = riskData.resolution || 0.5;
  const halfRes = resolution / 2;

  // Filter points by minimum risk level
  const filteredPoints = riskData.points.filter(p =>
    RISK_LEVEL_ORDER.indexOf(p.riskLevel) >= minLevelIndex
  );

  if (filteredPoints.length === 0) return null;

  return (
    <>
      {filteredPoints.map((point) => {
        const colors = RISK_COLORS[point.riskLevel] || RISK_COLORS.moderate;

        const bounds: [[number, number], [number, number]] = [
          [point.lat - halfRes, point.lon - halfRes],
          [point.lat + halfRes, point.lon + halfRes]
        ];

        return (
          <Rectangle
            key={`${point.lat}-${point.lon}`}
            bounds={bounds}
            pathOptions={{
              color: colors.fill,
              fillColor: colors.fill,
              fillOpacity: colors.opacity,
              weight: 0,
              opacity: 0
            }}
          >
            <Popup maxWidth={400} minWidth={320}>
              <div className="lightning-risk-popup">
                {/* Header - colored banner */}
                <div style={{
                  backgroundColor: colors.fill,
                  color: 'white',
                  padding: '12px 16px',
                }}>
                  <div style={{
                    fontSize: '10px',
                    opacity: 0.9,
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '500'
                  }}>
                    Lightning Risk
                  </div>
                  <div style={{ fontWeight: '600', fontSize: '15px', lineHeight: '1.3', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>&#9889;</span>
                    {point.riskLevel.charAt(0).toUpperCase() + point.riskLevel.slice(1)} Risk
                  </div>
                </div>

                {/* Body content */}
                <div style={{ padding: '14px 16px', backgroundColor: 'var(--bg-elevated)' }}>
                  {/* Risk badge */}
                  <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{
                      backgroundColor: colors.fill,
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '14px',
                      fontSize: '11px',
                      fontWeight: '600',
                    }}>
                      SCORE: {point.riskScore}/100
                    </span>
                    <span style={{
                      backgroundColor: 'var(--bg-tertiary)',
                      color: 'var(--text-secondary)',
                      padding: '4px 12px',
                      borderRadius: '14px',
                      fontSize: '11px',
                      fontWeight: '500',
                      border: '1px solid var(--border-color)',
                    }}>
                      CAPE: {point.cape.toLocaleString()} J/kg
                    </span>
                  </div>

                  {/* Storm potential explanation */}
                  <div style={{
                    fontSize: '13px',
                    marginBottom: '12px',
                    padding: '10px 12px',
                    backgroundColor: 'var(--bg-tertiary)',
                    borderRadius: '6px',
                    borderLeft: `3px solid ${colors.fill}`,
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>
                      Storm Potential
                    </div>
                    <div style={{ color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                      {point.cape >= 2500 && 'Very high - severe thunderstorms likely'}
                      {point.cape >= 1500 && point.cape < 2500 && 'Significant - strong thunderstorms possible'}
                      {point.cape >= 750 && point.cape < 1500 && 'Moderate - scattered thunderstorms possible'}
                      {point.cape >= 250 && point.cape < 750 && 'Low - isolated thunderstorms possible'}
                      {point.cape < 250 && 'Minimal - thunderstorms unlikely'}
                    </div>
                  </div>

                  {/* Fire warning for high/extreme */}
                  {(point.riskLevel === 'high' || point.riskLevel === 'extreme') && (
                    <div style={{
                      padding: '10px 12px',
                      backgroundColor: 'rgba(220, 38, 38, 0.1)',
                      border: '1px solid rgba(220, 38, 38, 0.3)',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#dc2626',
                      lineHeight: '1.5',
                    }}>
                      <strong>&#9888; Fire Ignition Risk</strong>
                      <div style={{ marginTop: '4px', fontSize: '11px', opacity: 0.9 }}>
                        Conditions favor thunderstorms. Lightning may ignite fires in dry vegetation.
                      </div>
                    </div>
                  )}

                  {/* Data source */}
                  <div style={{
                    marginTop: '12px',
                    fontSize: '10px',
                    color: 'var(--text-muted)',
                    paddingTop: '10px',
                    borderTop: '1px solid var(--border-color)',
                  }}>
                    Source: NOAA GFS (Global Forecast System)
                  </div>
                </div>
              </div>
            </Popup>
          </Rectangle>
        );
      })}
    </>
  );
};

export default LightningRiskLayer;
